"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authClient } from "@/lib/auth-client";
import { normalizePhoneNumber } from "@/lib/phone";

type AuthResult = {
  data?: unknown;
  error?: { message?: string; status?: number } | null;
};

/**
 * OAuth 인가 도중이면 서버가 `{ redirect: true, url }` 을 돌려주고 better-auth 기본
 * redirect 플러그인이 이미 그 주소로 이동시킨다(여기서 또 이동하면 인가 코드 주소를 두 번 열 수 있다).
 * 그렇지 않으면 첫 화면으로 간다.
 */
function useAfterSignIn() {
  const router = useRouter();
  return (result: AuthResult) => {
    const data = result.data as { redirect?: boolean; url?: string } | null;
    if (data?.redirect && data.url) return;
    router.replace("/");
    router.refresh();
  };
}

function errorMessage(error: AuthResult["error"], fallback: string) {
  if (!error) return fallback;
  if (error.status === 429) return "요청이 너무 많습니다. 잠시 뒤 다시 시도해 주세요.";
  return error.message || fallback;
}

const signInSchema = z.object({
  email: z.email("이메일 주소를 확인해 주세요"),
  password: z.string().min(1, "비밀번호를 입력해 주세요"),
});

function EmailSignInForm() {
  const afterSignIn = useAfterSignIn();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const result = await authClient.signIn.email(values);
    if (result.error) {
      setFormError(
        result.error.status === 401
          ? "이메일 또는 비밀번호가 맞지 않습니다."
          : errorMessage(result.error, "로그인하지 못했습니다."),
      );
      return;
    }
    afterSignIn(result);
  });

  const { errors, isSubmitting } = form.formState;
  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup className="gap-4">
        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="signin-email">이메일</FieldLabel>
          <Input
            id="signin-email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...form.register("email")}
          />
          <FieldError errors={[errors.email]} />
        </Field>
        <Field data-invalid={!!errors.password}>
          <FieldLabel htmlFor="signin-password">비밀번호</FieldLabel>
          <Input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            {...form.register("password")}
          />
          <FieldError errors={[errors.password]} />
        </Field>
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "로그인 중…" : "로그인"}
        </Button>
      </FieldGroup>
    </form>
  );
}

const currentYear = new Date().getFullYear();

const signUpSchema = z.object({
  name: z.string().trim().min(1, "이름을 입력해 주세요").max(100),
  email: z.email("이메일 주소를 확인해 주세요"),
  password: z.string().min(8, "비밀번호는 8자 이상이어야 합니다").max(128),
  nickname: z.string().trim().max(30, "닉네임은 30자 이하로 입력해 주세요"),
  birthYear: z
    .string()
    .trim()
    .refine(
      (value) =>
        value === "" ||
        (/^\d{4}$/.test(value) &&
          Number(value) >= 1900 &&
          Number(value) <= currentYear),
      "출생연도 네 자리를 확인해 주세요",
    ),
});

function SignUpForm() {
  const afterSignIn = useAfterSignIn();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      nickname: "",
      birthYear: "",
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const result = await authClient.signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
      nickname: values.nickname || undefined,
      birthYear: values.birthYear ? Number(values.birthYear) : undefined,
      locale: "ko",
    });
    if (result.error) {
      setFormError(errorMessage(result.error, "가입하지 못했습니다."));
      return;
    }
    afterSignIn(result);
  });

  const { errors, isSubmitting } = form.formState;
  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup className="gap-4">
        <Field data-invalid={!!errors.name}>
          <FieldLabel htmlFor="signup-name">이름</FieldLabel>
          <Input
            id="signup-name"
            autoComplete="name"
            aria-invalid={!!errors.name}
            {...form.register("name")}
          />
          <FieldError errors={[errors.name]} />
        </Field>
        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="signup-email">이메일</FieldLabel>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...form.register("email")}
          />
          <FieldError errors={[errors.email]} />
        </Field>
        <Field data-invalid={!!errors.password}>
          <FieldLabel htmlFor="signup-password">비밀번호</FieldLabel>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.password}
            {...form.register("password")}
          />
          <FieldDescription>8자 이상</FieldDescription>
          <FieldError errors={[errors.password]} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field data-invalid={!!errors.nickname}>
            <FieldLabel htmlFor="signup-nickname">닉네임 (선택)</FieldLabel>
            <Input
              id="signup-nickname"
              autoComplete="nickname"
              aria-invalid={!!errors.nickname}
              {...form.register("nickname")}
            />
            <FieldError errors={[errors.nickname]} />
          </Field>
          <Field data-invalid={!!errors.birthYear}>
            <FieldLabel htmlFor="signup-birth-year">출생연도 (선택)</FieldLabel>
            <Input
              id="signup-birth-year"
              inputMode="numeric"
              placeholder="예: 1990"
              aria-invalid={!!errors.birthYear}
              {...form.register("birthYear")}
            />
            <FieldError errors={[errors.birthYear]} />
          </Field>
        </div>
        <FieldDescription>
          만 14세 미만은 보호자 계정에서 등록합니다(준비 중). 연결 서비스에는
          출생연도 대신 연령대만 전달됩니다.
        </FieldDescription>
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "가입 중…" : "가입하기"}
        </Button>
      </FieldGroup>
    </form>
  );
}

function PhoneSignInForm() {
  const afterSignIn = useAfterSignIn();
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{
    kind: "error" | "info";
    text: string;
  } | null>(null);

  async function sendCode() {
    const normalized = normalizePhoneNumber(phoneInput);
    if (!normalized) {
      setMessage({ kind: "error", text: "휴대폰 번호를 확인해 주세요." });
      return;
    }
    setPending(true);
    setMessage(null);
    const result = await authClient.phoneNumber.sendOtp({
      phoneNumber: normalized,
    });
    setPending(false);
    if (result.error) {
      setMessage({
        kind: "error",
        text: errorMessage(result.error, "인증번호를 보내지 못했습니다."),
      });
      return;
    }
    setPhoneNumber(normalized);
    setMessage({ kind: "info", text: "인증번호를 보냈습니다. 5분 안에 입력해 주세요." });
  }

  async function verifyCode() {
    if (!phoneNumber) return;
    setPending(true);
    setMessage(null);
    const result = await authClient.phoneNumber.verify({ phoneNumber, code });
    setPending(false);
    if (result.error) {
      setMessage({
        kind: "error",
        text:
          (result.error as { code?: string }).code === "PHONE_NUMBER_NOT_REGISTERED"
            ? "이 휴대폰 번호로 등록된 계정이 없습니다. 이메일로 로그인한 뒤 첫 화면에서 번호를 등록해 주세요."
            : errorMessage(result.error, "인증하지 못했습니다. 인증번호를 확인해 주세요."),
      });
      return;
    }
    afterSignIn(result);
  }

  return (
    <FieldGroup className="gap-4">
      <Field>
        <FieldLabel htmlFor="phone-number">휴대폰 번호</FieldLabel>
        <div className="flex gap-2">
          <Input
            id="phone-number"
            type="tel"
            autoComplete="tel"
            placeholder="010-1234-5678"
            value={phoneInput}
            onChange={(event) => setPhoneInput(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={sendCode}
            disabled={pending}
          >
            {phoneNumber ? "다시 받기" : "인증번호 받기"}
          </Button>
        </div>
        <FieldDescription>
          계정에 등록·인증한 번호로만 로그인할 수 있습니다. 해외 번호는 +국가번호로
          입력해 주세요.
        </FieldDescription>
      </Field>
      {phoneNumber && (
        <Field>
          <FieldLabel htmlFor="phone-code">인증번호</FieldLabel>
          <Input
            id="phone-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </Field>
      )}
      {message && (
        <Alert variant={message.kind === "error" ? "destructive" : "default"}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}
      {phoneNumber && (
        <Button
          type="button"
          className="w-full"
          onClick={verifyCode}
          disabled={pending || code.length < 6}
        >
          {pending ? "확인 중…" : "로그인"}
        </Button>
      )}
    </FieldGroup>
  );
}

export function SignInPanel({
  defaultTab = "sign-in",
}: {
  defaultTab?: "sign-in" | "sign-up" | "phone";
}) {
  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="sign-in">이메일 로그인</TabsTrigger>
        <TabsTrigger value="phone">휴대폰</TabsTrigger>
        <TabsTrigger value="sign-up">회원가입</TabsTrigger>
      </TabsList>
      <TabsContent value="sign-in" className="pt-4">
        <EmailSignInForm />
      </TabsContent>
      <TabsContent value="phone" className="pt-4">
        <PhoneSignInForm />
      </TabsContent>
      <TabsContent value="sign-up" className="pt-4">
        <SignUpForm />
      </TabsContent>
    </Tabs>
  );
}
